import { Sorting } from '@devexpress/dx-react-grid'
import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import {
  Consolidation,
  ConsolidationInboundCreateInput,
  ConsolidationType,
} from '@returnmates/client-core/src/graphql/generated/api'
import {
  getConsolidations as getConsolidationsSelector,
  getConsolidationsCount as getConsolidationsCountSelector,
} from '@returnmates/client-core/src/selectors/admin'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import errorMapper from '@returnmates/client-core/src/utils/errorMapper'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import { FORM_ERROR } from 'final-form'
import moment from 'moment-timezone'
import { memo, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import ConsolidationsTable from '../../../components/ConsolidationsTable'
import { ConsolidationFilter } from '../../../components/ConsolidationsTable/types'
import { FLYOUT_SIDEBAR_TYPES, FlyoutSidebarTypes } from '../../../constants/flyoutSidebar'
import { Context } from '../../Main'
import { DEFAULT_SORT, SORT_DIRECTION_MAP, SORT_FIELDS_MAP } from '../constants'
import { getFormattedData } from '../utils'
import AddConsolidationModal from './AddConsolidationModal'

function ConsolidationsSection() {
  const dispatch = useDispatch()
  const consolidations = useSelector(getConsolidationsSelector)
  const consolidationsCount = useSelector(getConsolidationsCountSelector)
  const { currentDateRange, currentHubId } = useContext(Context)
  const [isLoading, setIsLoading] = useState(false)
  const [pageSize, setPageSize] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(0)

  const [currentPackagePage, setCurrentPackagePage] = useState<number>(0)
  const [pagePackageSize, setPagePackageSize] = useState<number>(0)

  const [sorting, setSorting] = useState<Sorting>(DEFAULT_SORT)
  const [filter, setFilter] = useState<ConsolidationFilter>({
    status: null,
    partnerCode: null,
    carrierId: null,
    consolidationId: null,
    locationId: null,
    addressId: null,
  })
  const [isAddConsolidationModalOpen, setIsAddConsolidationModalOpen] = useState(false)

  const onCurrentPackagePage = useCallback(value => {
    setCurrentPackagePage(value)
  }, [])

  const onCurrentPage = useCallback(value => {
    setCurrentPage(value)
  }, [])

  const openConsolidationDataRef = useRef<Consolidation | null>(null)

  const updateOpenConsolidationDataRef = useCallback(
    (val: Consolidation) => (openConsolidationDataRef.current = val),
    [],
  )

  const onSortingChange = useCallback(newSorting => {
    setSorting(newSorting || DEFAULT_SORT)
  }, [])

  const handleOpenAddConsolidationModal = useCallback(() => {
    setIsAddConsolidationModalOpen(true)
  }, [])

  const handleCloseAddConsolidationModal = useCallback(() => {
    setIsAddConsolidationModalOpen(false)
  }, [])

  const onFilterUpdate = useCallback(
    newFilter => {
      setCurrentPage(0)
      setFilter(newFilter)
    },
    [setCurrentPage],
  )

  const createConsolidation = useCallback(
    async ({
      hubId,
      partnerDestinationId,
      carrierId,
      expectedDeliveryDate,
    }: ConsolidationInboundCreateInput) => {
      setIsLoading(true)
      try {
        await createAsyncAction(
          dispatch,
          actions.createConsolidation.request({
            input: {
              hubId,
              partnerDestinationId,
              carrierId,
              expectedDeliveryDate: moment(expectedDeliveryDate).format('YYYY-MM-DD'),
            },
          }),
        )
        setIsAddConsolidationModalOpen(false)
      } catch (err) {
        const { message } = err as Error

        return {
          [FORM_ERROR]: errorMapper(message || (err as string)),
        }
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch],
  )

  const requestConsolidations = useCallback(async () => {
    setIsLoading(true)
    try {
      const searchFilter = Object.entries(filter).find(
        item =>
          (item[0] === 'destination' ||
            item[0] === 'packageId' ||
            item[0] === 'trackingId' ||
            item[0] === 'consolidationId') &&
          item[1],
      )
      const searchFilterWhere = searchFilter ? { [searchFilter[0]]: searchFilter[1] } : null

      await createAsyncAction(
        dispatch,
        actions.getConsolidations.request({
          where: searchFilterWhere || {
            fromDate: moment(currentDateRange.startDate).format('YYYY-MM-DD'),
            toDate: moment(currentDateRange.endDate).format('YYYY-MM-DD'),
            hubId: currentHubId,
            type: ConsolidationType.INBOUND,
            ...filter,
          },
          pagination: {
            size: pageSize,
            page: currentPage,
          },
          order: {
            direction: SORT_DIRECTION_MAP[sorting.direction],
            field: SORT_FIELDS_MAP[sorting.columnName as keyof typeof SORT_FIELDS_MAP],
          },
        }),
      )

      if (openConsolidationDataRef.current?.id) {
        await createAsyncAction(
          dispatch,
          actions.getConsolidationPackages.request({
            where: {
              consolidationId: openConsolidationDataRef.current?.id,
            },
            pagination: {
              size: 0,
              page: currentPackagePage,
            },
          }),
        )
      }
    } catch (err) {
      const { message } = err as Error

      dispatch(
        actions.addSnackBar.request({
          type: SnackBarStatuses.ERROR,
          message: errorMapper(message || (err as string)),
        }),
      )
    } finally {
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentDateRange.endDate,
    currentDateRange.startDate,
    currentHubId,
    currentPage,
    dispatch,
    filter,
    pageSize,
    sorting.columnName,
    sorting.direction,
    currentPackagePage,
  ])

  useEffect(() => {
    if (location.search) {
      const searchParams = new URLSearchParams(location.search)
      const locationId = searchParams.get('locationId')

      if (locationId) {
        onFilterUpdate({
          //@ts-ignore
          locationId,
          fromDate: undefined,
          toDate: undefined,
          hubId: undefined,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, onFilterUpdate])

  useEffect(() => {
    dispatch(actions.getCarriers.request())
  }, [dispatch])

  useEffect(() => {
    if (pageSize) {
      requestConsolidations()
    }
  }, [pageSize, requestConsolidations, pagePackageSize])

  return (
    <div>
      <ConsolidationsTable
        data={getFormattedData(consolidations || [])}
        totalCount={consolidationsCount || 0}
        onFilterUpdate={onFilterUpdate}
        onSortingChange={onSortingChange}
        onPageSizeChange={setPageSize}
        currentPackagePage={currentPackagePage}
        setCurrentPackagePage={onCurrentPackagePage}
        onCurrentPageChange={onCurrentPage}
        currentPageNumber={currentPage}
        isLoading={isLoading}
        updateOpenConsolidationDataRef={updateOpenConsolidationDataRef}
        handleOpenAddConsolidationModal={handleOpenAddConsolidationModal}
        tableType={FLYOUT_SIDEBAR_TYPES.EDIT_CONSOLIDATION as FlyoutSidebarTypes.EDIT_CONSOLIDATION}
      />

      <AddConsolidationModal
        isOpen={isAddConsolidationModalOpen}
        onClose={handleCloseAddConsolidationModal}
        onCreate={createConsolidation}
        isLoading={isLoading}
        isInboundModal
      />
    </div>
  )
}

export default memo(ConsolidationsSection)
